<template>
  <div>
    <h2>Berichte</h2>
    <p>
      Hier entstehen die Dokumente, die das Projekt verlassen: Berichtstyp wählen,
      gegebenenfalls Anhänge ankreuzen, <b>Bericht erstellen</b> drücken — die Datei wird
      heruntergeladen. Gerechnet wird dabei mit dem Stand, der auf den Projektseiten
      gespeichert ist.
    </p>

    <h3>Kurz zum Start</h3>
    <ol>
      <li>Berichtstyp auswählen (siehe Tabelle unten).</li>
      <li>Nur beim Einsparpotentialbericht: <b>Analyse-Anhang mit Diagrammen</b> einschalten, wenn die
        Grafiken der anderen Seiten mitsollen.</li>
      <li><b>Bericht erstellen</b> — die Erstellung dauert einen Moment, weil die Diagramme
        dafür im Hintergrund gezeichnet werden.</li>
    </ol>

    <h3>Die vier Berichtstypen</h3>
    <table>
      <thead>
        <tr>
          <th>Typ</th>
          <th>Format</th>
          <th>Wofür</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Einsparpotentialbericht</td>
          <td>Word</td>
          <td>Der ausführliche Projektbericht über alle Maßnahmen und Gruppen — der
            übliche Standardbericht.</td>
        </tr>
        <tr>
          <td>Energieaudit DIN EN 16247-1</td>
          <td>Word</td>
          <td>Bericht in der Gliederung, die die Auditnorm verlangt.</td>
        </tr>
        <tr>
          <td>Maßnahmen-Kurzberichte</td>
          <td>ZIP</td>
          <td>Je Maßnahme ein eigenes kurzes Dokument, gesammelt in einem Archiv —
            praktisch zum Weitergeben an einzelne Gewerke. Als <b>Vorlage</b> gedacht:
            die Tabellen dürfen nach dem Download angepasst und um eigenen Text
            ergänzt werden.</td>
        </tr>
        <tr>
          <td>Bewertungsberichte DIN EN 17463 / VALERI</td>
          <td>ZIP</td>
          <td>Wirtschaftlichkeitsbewertung je Maßnahme nach der VALERI-Norm,
            einschließlich Sensitivitätsanalyse.</td>
        </tr>
      </tbody>
    </table>

    <h3>Normen und Abkürzungen</h3>
    <dl>
      <dt><strong>DIN EN 16247-1</strong> — Energieaudits</dt>
      <dd>Legt Ablauf und Berichtsgliederung eines Energieaudits fest: Bestandsaufnahme,
        Analyse, Maßnahmenvorschläge. Dieser Bericht ist der übliche Nachweis gegenüber
        Behörden und Auditoren.</dd>

      <dt><strong>DIN EN 17463</strong> — VALERI</dt>
      <dd>„Valuation of Energy Related Investments". Beschreibt, wie energiebezogene
        Investitionen einheitlich bewertet werden — abgezinste Zahlungsströme über die
        Laufzeit, betrachtet in mehreren Szenarien statt in einer einzelnen Zahl.</dd>

      <dt><strong>Sensitivitätsanalyse</strong></dt>
      <dd>Nur beim VALERI-Bericht. Jede Eingangsgröße wird um den eingestellten Prozentsatz
        nach oben und unten ausgelenkt; der Bericht zeigt, welche Größe den Kapitalwert am
        stärksten bewegt. Vorgabe ist 15 %.</dd>

      <dt><strong>Anhang / Module</strong></dt>
      <dd>Die Diagramme der Seiten Energiebilanz, Benchmarking und VALERI, die dem Bericht
        hinten angefügt werden.</dd>
    </dl>

    <v-expansion-panels accordion class="mt-5">
      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Anhänge mit Diagrammen</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p>
            Einsparpotentialbericht und Energieaudit können die Diagramme der übrigen Projektseiten
            aufnehmen. Angekreuzt werden sie unter <b>Anhänge hinzufügen</b>:
          </p>
          <ul>
            <li><b>Energiebilanz (Sankey)</b> — Energieflüsse Referenz und Geplant.</li>
            <li><b>Benchmarking</b> — flächenbezogene Kennwerte.</li>
            <li><b>VALERI (Wirtschaftlichkeit)</b> — Kapitalwert, Amortisation, Annuität.</li>
          </ul>
          <p>
            Diese Seiten müssen vorher <b>nicht</b> geöffnet worden sein: die Diagramme
            werden beim Erstellen im Hintergrund gezeichnet. Das ist der Grund, warum der
            Vorgang einige Sekunden braucht.
          </p>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title>
          <div class="heading">Wenn etwas fehlt</div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <ul>
            <li>
              Auf der Seite
              <router-link
                v-if="projectId"
                :to="{ name: 'ProjectDashboard', params: { projectId: String(projectId) } }"
              >Maßnahmen</router-link><span v-else>Maßnahmen</span>
              abgewählte Maßnahmen erscheinen in keinem Bericht.
            </li>
            <li>
              Leere oder auffällige Zahlen im Bericht kommen aus den Projektseiten, nicht aus
              dem Bericht selbst. Erst dort prüfen — fehlende Energiepreise oder eine fehlende
              Bruttogrundfläche sind die häufigsten Ursachen.
            </li>
            <li>
              Die VALERI-Parameter stammen aus der Seite
              <router-link
                v-if="projectId"
                :to="{ name: 'ProjectCalcValeri', params: { projectId: String(projectId) } }"
              >VALERI</router-link><span v-else>VALERI</span>
              und müssen dort über <b>Werte Speichern</b> abgelegt sein, damit der Bericht
              mit ihnen rechnet.
            </li>
          </ul>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
/*
 * `projectId` aus dem Store: die Verweise auf Maßnahmen- und VALERI-Seite
 * verlangen den Routenparameter. Gleicher Aufbau wie in `ProjectCalcHelp.vue`.
 */
import { computed } from 'vue';
import { useProjectStore } from '@/stores/projectStore';

const store = useProjectStore();
const projectId = computed(() => store.currentProject?.id ?? null);
</script>

<style scoped></style>
